import React, { forwardRef, useState, useMemo } from "react";
import { SectionCard, Icon, C } from "../shared";

const PAGE_SIZE = 5;
const TABS = ["All", "Financial Results", "Others"];

const pillStyle = (type) => {
  if (type === "Financial Results") return { background: "rgba(249,131,0,0.12)", color: C.orange };
  if (type === "Board Meetings")    return { background: "rgba(34,197,94,0.12)", color: C.green };
  return { background: "rgba(91,141,239,0.12)", color: C.blue };
};

const AnnouncementsSection = forwardRef(({ announcements }, ref) => {
  const [tab, setTab]   = useState("All");
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    if (tab === "All") return announcements;
    if (tab === "Financial Results")
      return announcements.filter(a => a.announcement_type_name === "Financial Results");
    // Others = everything that's not a Financial Result
    return announcements.filter(a => a.announcement_type_name !== "Financial Results");
  }, [announcements, tab]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage   = Math.min(page, totalPages);
  const start      = (safePage - 1) * PAGE_SIZE;
  const rows       = filtered.slice(start, start + PAGE_SIZE);

  const selectTab = (t) => { setTab(t); setPage(1); };

  return (
    <SectionCard
      ref={ref} id="announcements" icon="announcements" title="Salient Announcements" noPad
      right={
        <div style={{ display: "flex", gap: 6, padding: "0 14px 0 0", flexWrap: "wrap" }}>
          {TABS.map(t => (
            <button
              key={t}
              className={`tdash-rbtn${tab === t ? " active" : ""}`}
              onClick={() => selectTab(t)}
            >
              {t}
            </button>
          ))}
        </div>
      }
    >
      <div style={{ overflowX: "auto" }}>
        <table className="tdash-table" style={{ minWidth: 560 }}>
          <thead>
            <tr>
              <th style={{ textAlign: "left" }}>Announcement</th>
              <th style={{ textAlign: "left" }}>Date</th>
              <th style={{ textAlign: "left" }}>Subject</th>
              <th style={{ textAlign: "center" }}>PDF</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={4} className="tdash-muted">No announcements in this category</td></tr>
            ) : rows.map((ann) => (
              <tr key={ann.notice_id}>
                <td style={{ whiteSpace: "nowrap" }}>
                  <span className="tdash-pill" style={pillStyle(ann.announcement_type_name)}>{ann.announcement_type_name}</span>
                </td>
                <td style={{ whiteSpace: "nowrap", color: C.muted, fontSize: 12 }}>{ann.announcement_date}</td>
                <td style={{ color: C.body }}>
                  {ann.description}
                  {ann.is_revised && <span style={{ marginLeft: 6, fontSize: 10, color: C.orange, fontWeight: 600 }}>(Revised)</span>}
                </td>
                <td style={{ textAlign: "center" }}>
                  {ann.pdf_url
                    ? <a className="tdash-pdf" href={ann.pdf_url} target="_blank" rel="noopener noreferrer"
                         style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                        <Icon name="financials" size={14} /> PDF
                      </a>
                    : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* pagination */}
      {filtered.length > PAGE_SIZE && (
        <div className="tdash-pager">
          <span className="tdash-pager-info">
            {start + 1}–{Math.min(start + PAGE_SIZE, filtered.length)} of {filtered.length}
          </span>
          <div style={{ display: "flex", gap: 6 }}>
            <button className="tdash-pg-btn" disabled={safePage === 1} onClick={() => setPage(p => Math.max(1, p - 1))}>
              Prev
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
              <button
                key={n}
                className={`tdash-pg-btn${n === safePage ? " active" : ""}`}
                onClick={() => setPage(n)}
              >
                {n}
              </button>
            ))}
            <button className="tdash-pg-btn" disabled={safePage === totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>
              Next
            </button>
          </div>
        </div>
      )}
    </SectionCard>
  );
});

export default AnnouncementsSection;
